import { createApiClient } from './client'
import type { GeoJSON } from 'geojson'

// Route types
export interface Route {
  id: string
  name: string
  description?: string
  geometry?: any // GeoJSON geometry
  is_public: boolean
  created_by: string
  created_at: string
  updated_at: string
  // Styling fields
  color?: string // Hex color (default: '#3b82f6')
  line_type?: 'solid' | 'dashed' | 'dotted' // Line type (default: 'solid')
  line_width?: number // Line width in pixels (default: 4)
}

export interface RouteResponse extends Route {
  associated_trips?: Array<{
    trip_id: string
    trip_name: string
    added_at: string
    notes?: string
    planned_date?: string | null
    planned_start_time?: string | null
    planned_end_time?: string | null
    planned_order?: number | null
  }>
  waypoint_count?: number
  distance?: number // Distance in meters
  duration?: number // Duration in seconds
  elevation_gain?: number // Elevation gain in meters (if available)
  elevation_loss?: number // Elevation loss in meters (if available)
  difficulty?: string | null
  metadata?: Record<string, any> | null
  tags?: string[] | null
  // Itinerary day metadata (for organized routes)
  day?: number // Day number for itinerary organization
  color?: string // Color for day visualization
}

export interface MultipleRoutesResponse {
  routes: RouteResponse[]
  importType: string
  routeCount: number
  message: string
}

export interface WaypointImportResponse {
  success: boolean
  importedWaypoints: WaypointResponse[]
  importType: 'waypoints'
  waypointCount: number
  message: string
}

export type WaypointSource = 'gpx_import' | 'manual' | 'campsite_discovery' | 'poi_search'

export interface Waypoint {
  id: string
  name: string
  latitude?: number
  longitude?: number
  elevation?: number | null
  notes?: string
  // POI-specific fields
  poi_type?: 'gas' | 'water' | 'viewpoint' | 'campsite' | 'meetup_spot' | 'trailhead' | 'custom' | null
  poi_custom_label?: string // Custom label text when poi_type is 'custom'
  poi_mapbox_id?: string
  poi_address?: string
  poi_metadata?: Record<string, any>
  created_at: string
  // Source and folder fields
  source?: WaypointSource
  folder_id?: string | null
}

export interface WaypointResponse extends Waypoint {
  trip_name?: string
  route_name?: string
  source_type?: string
  // Direct associations (for filtering and queries)
  trip_id?: string | null // Associated trip ID (if any)
  route_id?: string | null // Associated route ID (if any)
  // Trip-specific metadata from junction table (when queried with trip context)
  // These fields are populated from trip_waypoints junction table
  trip_day?: number // trip_day from trip_waypoints junction
  order_index?: number // order_index from trip_waypoints junction
  start_time?: string // start_time from trip_waypoints junction (trip-specific time slots)
  end_time?: string // end_time from trip_waypoints junction (trip-specific time slots)
  is_poi?: boolean // is_poi from trip_waypoints junction (true for POI waypoints, false for itinerary waypoints)
  // Aliases for backward compatibility
  junction_trip_day?: number // Same as trip_day
  junction_order_index?: number // Same as order_index
}

export interface AmenitySummary {
  id: string
  name: string
  poi_type: string
  distanceMeters: number
}

export interface CampsiteCandidate {
  coordinates: [number, number]
  roadId: string
  roadName?: string
  highwayType?: string
  surface?: string
  classification: 'dead_end' | 'turn'
  distanceFromMainRoad?: number
  waterBody?: {
    id: string
    name?: string
    type: string
    distanceMeters: number
  }
  mvumStatus?: {
    status: 'open' | 'restricted' | 'closed' | 'seasonal' | 'unknown'
    mvumRoadNumber?: string
    mvumName?: string
    allowedVehicleClasses?: string[]
    seasonalStart?: string | null
    seasonalEnd?: string | null
    restrictions?: string | null
    dataSource: 'mvum'
  } | null
  landOwnership?: {
    designation: string
    agency?: string
    unitName?: string
    campingAllowed: boolean
    notes?: string
    dataSource: 'public_lands'
  } | null
  terrain?: {
    slopeDegrees: number | null
    meanElevation: number | null
    dataSource: 'mapbox_terrain'
  } | null
  hazards: {
    flood?: {
      inFloodplain: boolean
      floodZone?: string
      floodDesignation?: string
      dataSource: 'fema_nfhl'
    } | null
    wildfire?: {
      withinPerimeter: boolean
      perimeterName?: string
      year?: number
      containmentPercent?: number
      daysSinceContained?: number | null
      dataSource: 'wildfire_perimeters'
    } | null
  }
  amenities: {
    supporting: AmenitySummary[]
    conflicting: AmenitySummary[]
  }
  scoringBreakdown: {
    water: number
    roadAccess: number
    terrain: number
    hazards: number
    amenities: number
    spurQuality: number
  }
  totalScore: number
  metadata?: {
    intersectingRoadId?: string
    intersectingRoadName?: string
    intersectionDistance?: number
    spurLength?: number
    spurAngle?: number
  }
}

export interface TripRoute {
  id: string
  trip_id: string
  route_id: string
  added_by: string
  added_at: string
  notes?: string
  planned_date?: string | null
  planned_start_time?: string | null
  planned_end_time?: string | null
  planned_order?: number | null
  start_latitude?: number | null
  start_longitude?: number | null
  end_latitude?: number | null
  end_longitude?: number | null
}

export interface TripRouteScheduleRoute {
  association: TripRoute
  route: RouteResponse
  waypoints: WaypointResponse[]
}

export interface TripRouteScheduleDay {
  date: string
  dayIndex: number
  label: string
  routes: TripRouteScheduleRoute[]
}

export interface TripRouteScheduleResponse {
  tripId: string
  startDate: string | null
  endDate: string | null
  days: TripRouteScheduleDay[]
  unscheduled: TripRouteScheduleRoute[]
}

export interface UpdateTripRouteAssociationDto {
  notes?: string | null
  planned_date?: string | null
  planned_start_time?: string | null
  planned_end_time?: string | null
  planned_order?: number | null
}

// DTOs
export interface CreateRouteDto {
  name: string
  description?: string
  geometry?: any
  is_public?: boolean
  source_type?: 'manual' | 'gpx_import' | 'timeline'
  // Styling fields
  color?: string
  line_type?: 'solid' | 'dashed' | 'dotted'
  line_width?: number
}

export interface UpdateRouteDto {
  name?: string
  description?: string
  geometry?: any
  is_public?: boolean
  // Styling fields
  color?: string
  line_type?: 'solid' | 'dashed' | 'dotted'
  line_width?: number
}

export interface AssociateRouteToTripDto {
  trip_id: string
  route_id: string
  notes?: string
  planned_date?: string | null
  planned_start_time?: string | null
  planned_end_time?: string | null
  planned_order?: number | null
}

export interface CreateWaypointDto {
  name: string
  latitude?: number
  longitude?: number
  notes?: string
  // POI-specific fields
  poi_type?: 'gas' | 'water' | 'viewpoint' | 'campsite' | 'meetup_spot' | 'trailhead' | 'custom' | null
  poi_custom_label?: string // Custom label text when poi_type is 'custom'
  poi_mapbox_id?: string
  poi_address?: string
  poi_metadata?: Record<string, any>
  // Source tracking
  source_type?: 'manual' | 'gpx_import' | 'route_template'
  // Source and folder (source is system-assigned, folder_id is user-assigned)
  source?: WaypointSource
  folder_id?: string | null
  // Trip association fields (stored in trip_waypoints junction table)
  trip_id?: string | null
  trip_day?: number | null
  order_index?: number
  // POI flag (stored in trip_waypoints junction table)
  is_poi?: boolean
  // Route association (optional, for waypoint-first model)
  route_id?: string | null
}

export interface UpdateWaypointDto {
  name?: string
  latitude?: number
  longitude?: number
  notes?: string
  // POI-specific fields
  poi_type?: 'gas' | 'water' | 'viewpoint' | 'campsite' | 'meetup_spot' | 'trailhead' | 'custom' | null
  poi_custom_label?: string // Custom label text when poi_type is 'custom'
  poi_mapbox_id?: string
  poi_address?: string
  poi_metadata?: Record<string, any>
  // Folder assignment (users can change folder)
  folder_id?: string | null
  // Trip-specific time fields (from junction table)
  start_time?: string | null
  end_time?: string | null
  order_index?: number
}

export interface GetRoutesQueryDto {
  trip_id?: string
  is_public?: boolean
  search?: string
  limit?: number
  offset?: number
}

export interface GetWaypointsQueryDto {
  trip_id?: string
  route_id?: string
  aggregate?: boolean
}

// Routes API class
export class RoutesApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  /**
   * @deprecated Routes are now drawn client-side, not fetched from backend
   */
  async getRoutes(query?: GetRoutesQueryDto): Promise<RouteResponse[]> {
    return []
  }

  // Trip-Route associations
  async associateRouteToTrip(
    routeId: string,
    tripId: string,
    association?: {
      notes?: string
      planned_date?: string | null
      planned_start_time?: string | null
      planned_end_time?: string | null
      planned_order?: number | null
    }
  ): Promise<TripRoute> {
    const payload: AssociateRouteToTripDto = {
      trip_id: tripId,
      route_id: routeId
    }

    if (association) {
      if (association.notes !== undefined) {
        payload.notes = association.notes
      }
      if (association.planned_date !== undefined) {
        payload.planned_date = association.planned_date
      }
      if (association.planned_start_time !== undefined) {
        payload.planned_start_time = association.planned_start_time
      }
      if (association.planned_end_time !== undefined) {
        payload.planned_end_time = association.planned_end_time
      }
      if (association.planned_order !== undefined && association.planned_order !== null) {
        payload.planned_order = association.planned_order
      }
    }

    return await this.apiClient.post<TripRoute>(`/itinerary/${routeId}/trips`, payload)
  }

  async dissociateRouteFromTrip(routeId: string, tripId: string): Promise<void> {
    await this.apiClient.delete(`/itinerary/${routeId}/trips/${tripId}`)
  }

  async getTripsByRoute(routeId: string): Promise<Array<{ trip_id: string; trip_name: string; added_at: string; notes?: string; planned_date?: string | null; planned_start_time?: string | null; planned_end_time?: string | null; planned_order?: number | null }>> {
    return await this.apiClient.get<Array<{ trip_id: string; trip_name: string; added_at: string; notes?: string; planned_date?: string | null; planned_start_time?: string | null; planned_end_time?: string | null; planned_order?: number | null }>>(`/itinerary/${routeId}/trips`)
  }

  /**
   * @deprecated Routes are now drawn client-side
   */
  async getTripRouteSchedule(tripId: string): Promise<TripRouteScheduleResponse> {
    return {
      tripId,
      startDate: null,
      endDate: null,
      days: [],
      unscheduled: []
    }
  }

  async updateTripRouteAssociation(associationId: string, updates: UpdateTripRouteAssociationDto): Promise<TripRouteScheduleRoute> {
    const response = await this.apiClient.patch<{ success: boolean; data: TripRouteScheduleRoute }>(
      `/itinerary/trip-routes/${associationId}`,
      updates
    )
    return response.data
  }

  // Waypoint operations
  async createWaypoint(waypointData: CreateWaypointDto): Promise<WaypointResponse> {
    // If route_id is null or undefined, create as waypoint without route
    // Routes are generated later from waypoints
    if (!waypointData.route_id || waypointData.route_id === 'undefined' || waypointData.route_id === 'null') {
      // Use the general waypoints endpoint
      return await this.apiClient.post<WaypointResponse>('/itinerary/waypoints', waypointData)
    }
    return await this.apiClient.post<WaypointResponse>(`/itinerary/${waypointData.route_id}/waypoints`, waypointData)
  }

  async getWaypoints(query: GetWaypointsQueryDto, options?: { cache?: RequestCache }): Promise<WaypointResponse[]> {
    // Check if route_id exists and is not undefined/null
    if (query.route_id && query.route_id !== 'undefined' && query.route_id !== 'null') {
      return await this.apiClient.get<WaypointResponse[]>(`/itinerary/${query.route_id}/waypoints`, query, options)
    }
    // Fallback for general waypoint queries (standalone waypoints or trip-level queries)
    return await this.apiClient.get<WaypointResponse[]>('/itinerary/waypoints', query, options)
  }

  async updateWaypoint(waypointId: string, updates: UpdateWaypointDto, routeId: string): Promise<WaypointResponse> {
    // If routeId is provided and not empty, use the nested route
    // Otherwise, use the standalone waypoint endpoint
    if (routeId && routeId !== '' && routeId !== 'standalone') {
      return await this.apiClient.put<WaypointResponse>(`/itinerary/${routeId}/waypoints/${waypointId}`, updates)
    } else {
      return await this.apiClient.put<WaypointResponse>(`/itinerary/waypoints/${waypointId}`, updates)
    }
  }

  async deleteWaypoint(waypointId: string, routeId?: string): Promise<void> {
    // If routeId is provided and not 'standalone', use the nested route
    // Otherwise, use the standalone waypoint endpoint
    if (routeId && routeId !== 'standalone') {
      await this.apiClient.delete(`/itinerary/${routeId}/waypoints/${waypointId}`)
    } else {
      await this.apiClient.delete(`/itinerary/waypoints/${waypointId}`)
    }
  }

  // Convenience methods for common operations
  async getWaypointsByTripAndRoute(tripId: string, routeId: string): Promise<WaypointResponse[]> {
    return await this.getWaypoints({ trip_id: tripId, route_id: routeId })
  }

  async getAggregateWaypointsForRoute(routeId: string): Promise<WaypointResponse[]> {
    return await this.getWaypoints({ route_id: routeId, aggregate: true })
  }

  async getClusteredWaypoints(params: {
    zoom: number;
    north: number;
    south: number;
    east: number;
    west: number;
    trip_id?: string;
    route_id?: string;
  }): Promise<WaypointResponse[]> {
    return await this.apiClient.get<WaypointResponse[]>('/itinerary/waypoints/clustered', params)
  }

  // GPX import methods
  async uploadGpx(file: File): Promise<{
    success: boolean
    data: {
      waypointCount: number
      routePointCount: number
      routeName?: string
      routeDescription?: string
      metadata?: Record<string, any>
      waypoints: Array<{
        lat: number
        lon: number
        name?: string
        notes?: string
        elevation?: number
        metadata?: Record<string, any>
      }>
      routePoints: Array<{
        lat: number
        lon: number
        name?: string
        notes?: string
        elevation?: number
        metadata?: Record<string, any>
      }>
    }
  }> {
    const formData = new FormData()
    formData.append('gpxFile', file)
    return await this.apiClient.post('/itinerary/upload-gpx', formData)
  }

  /**
   * Import a GPX file into the user's library. The raw GPX XML is parsed
   * server-side (see backend trips/gpxImportService); waypoints land in a new
   * library folder named after the file (or `folderName` if provided).
   */
  async importGpx(filename: string, gpxXml: string, folderName?: string): Promise<{
    success: boolean
    data: {
      folder: { id: string; name: string }
      waypoints: WaypointResponse[]
      waypointCount: number
    }
  }> {
    return await this.apiClient.post('/trips/library/import-gpx', {
      filename,
      gpxXml,
      ...(folderName ? { folderName } : {})
    })
  }

  // POI Search methods
  async searchPOI(lat: number, lng: number, categories: string[], radius: number = 5000, legalOnly?: boolean): Promise<POIResult[]> {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
      radius: radius.toString()
    })
    
    // Add categories as multiple query parameters
    categories.forEach(category => {
      params.append('categories', category)
    })
    
    // Add legal_only parameter if provided
    if (legalOnly !== undefined) {
      params.append('legal_only', legalOnly.toString())
    }
    
    return await this.apiClient.get<POIResult[]>(`/itinerary/poi/search?${params.toString()}`)
  }

  async searchPOIAlongRoute(
    routeId: string, 
    categories: string[], 
    maxDistance: number = 10000, 
    legalOnly?: boolean,
    tripId?: string,
    tripDay?: number
  ): Promise<POIResult[]> {
    return await this.apiClient.post<POIResult[]>(`/itinerary/${routeId}/poi/search-along`, {
      categories,
      maxDistance,
      ...(legalOnly !== undefined && { legal_only: legalOnly }),
      ...(tripId && { tripId }),
      ...(tripDay !== undefined && { tripDay })
    })
  }

  async forwardGeocode(
    query: string, 
    lat?: number, 
    lng?: number, 
    limit: number = 5,
    sessionToken?: string,
    returnFullDetails: boolean = false
  ): Promise<Array<{ name: string; address: string; latitude: number; longitude: number; id: string; category?: string; mapbox_id?: string }>> {
    const params = new URLSearchParams({
      query,
      limit: limit.toString(),
      return_full_details: returnFullDetails.toString()
    })
    
    if (lat !== undefined && lng !== undefined) {
      params.append('lat', lat.toString())
      params.append('lng', lng.toString())
    }
    
    if (sessionToken) {
      params.append('session_token', sessionToken)
    }
    
    return await this.apiClient.get<Array<{ name: string; address: string; latitude: number; longitude: number; id: string; category?: string; mapbox_id?: string }>>(`/itinerary/poi/forward-geocode?${params.toString()}`)
  }

  async retrieveSuggestionDetails(
    mapboxId: string,
    sessionToken: string,
    lat?: number,
    lng?: number
  ): Promise<{ name: string; address: string; latitude: number; longitude: number; id: string; category?: string }> {
    const params = new URLSearchParams({
      mapbox_id: mapboxId,
      session_token: sessionToken
    })
    
    if (lat !== undefined && lng !== undefined) {
      params.append('lat', lat.toString())
      params.append('lng', lng.toString())
    }
    
    return await this.apiClient.get<{ name: string; address: string; latitude: number; longitude: number; id: string; category?: string }>(`/itinerary/poi/retrieve-suggestion?${params.toString()}`)
  }

  async reverseGeocode(lng: number, lat: number): Promise<{ name: string }> {
    return await this.apiClient.get<{ name: string }>(`/itinerary/poi/reverse-geocode?lng=${lng}&lat=${lat}`)
  }

  // Get elevation for coordinates
  async getElevation(latitude: number, longitude: number): Promise<{ elevation: number | null }> {
    return await this.apiClient.get<{ elevation: number | null }>(`/itinerary/elevation?latitude=${latitude}&longitude=${longitude}`)
  }

  // Dispersed campsite finder – viewport-scoped GeoJSON layer for the explore map.
  // Backend clusters nothing; the client uses Mapbox native GeoJSON clustering.
  async getExploreCampsites(
    bbox: [number, number, number, number], // [minLng, minLat, maxLng, maxLat]
    zoom: number
  ): Promise<ExploreCampsitesResponse> {
    const [minLng, minLat, maxLng, maxLat] = bbox
    return await this.apiClient.get<ExploreCampsitesResponse>('/itinerary/explore-campsites', {
      minLng,
      minLat,
      maxLng,
      maxLat,
      zoom: Math.round(zoom)
    })
  }

  // Dead end discovery for campsite finding
  async findDeadEnds(
    bbox?: [number, number, number, number],
    options?: {
      routeGeometry?: GeoJSON.LineString | GeoJSON.MultiLineString
      radiusMiles?: number | null
      limit?: number // Max number of results to return
      waypointId?: string // Waypoint ID for waypoint-based search
      coordinates?: [number, number] // Coordinates [lng, lat] for waypoint-based search
    }
  ): Promise<DeadEndResponse> {
    // If waypoint-based search is provided, use waypoint search
    if (options?.waypointId || options?.coordinates) {
      return await this.apiClient.post<DeadEndResponse>('/itinerary/find-dead-ends', {
        waypointId: options.waypointId,
        coordinates: options.coordinates,
        radiusMiles: options.radiusMiles ?? 2.5, // default to 2.5 miles for waypoint searches
        limit: options.limit
      })
    }
    // If route geometry is provided, use route-based search
    if (options?.routeGeometry) {
      return await this.apiClient.post<DeadEndResponse>('/itinerary/find-dead-ends', {
        routeGeometry: options.routeGeometry,
        radiusMiles: options.radiusMiles ?? 5, // default to 5 miles
        limit: options.limit // Pass limit to backend
      })
    }
    // Otherwise, use bbox-based search (legacy)
    if (!bbox) {
      throw new Error('Either bbox, routeGeometry, waypointId, or coordinates must be provided')
    }
    return await this.apiClient.post<DeadEndResponse>('/itinerary/find-dead-ends', { 
      bbox,
      limit: options?.limit
    })
  }

  async validateForestRoadClick(
    longitude: number,
    latitude: number,
    tolerance?: number
  ): Promise<ForestRoadValidationResponse> {
    return await this.apiClient.post<ForestRoadValidationResponse>('/itinerary/validate-forest-road-click', {
      longitude,
      latitude,
      tolerance
    })
  }

  async getForestRoadsForBounds(bbox: [number, number, number, number]): Promise<{
    success: boolean
    type: 'FeatureCollection'
    features: Array<{
      type: 'Feature'
      properties: {
        id: string
        name?: string
        highway: string
        surface?: string
      }
      geometry: GeoJSON.LineString
    }>
    count: number
  }> {
    return await this.apiClient.post('/itinerary/forest-roads', { bbox })
  }

  /**
   * Live OSM dead ends for validation – bypasses cache, hits Overpass directly.
   * Returns roads + dead ends for double-checking detector output.
   */
  async getLiveDeadEnds(bbox: [number, number, number, number]): Promise<{
    success: boolean
    source: 'live'
    roads: GeoJSON.FeatureCollection
    deadEnds: GeoJSON.FeatureCollection
    deadEndsList: DeadEnd[]
    stats: { roadCount: number; nodeCount: number; deadEndCount: number; deadEnd: number; turn: number }
  }> {
    return await this.apiClient.post('/itinerary/live-dead-ends', { bbox })
  }

  async getPublicLandBoundaries(bbox: [number, number, number, number]): Promise<{
    success: boolean
    type: 'FeatureCollection'
    features: Array<{
      type: 'Feature'
      properties: {
        id: string
        name?: string
        boundaryType: 'national_park' | 'protected_area' | 'park' | 'nature_reserve' | 'recreation_ground'
        protectClass?: string
        protectionTitle?: string
        owner?: string
        operator?: string
        access?: string
        [key: string]: any
      }
      geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
    }>
    count: number
  }> {
    return await this.apiClient.post('/itinerary/public-lands', { bbox })
  }

  // Ranger AI analysis for campsite DeadEnds
  async analyzeDeadEnds(
    deadEnds: DeadEnd[],
    options?: {
      tripId?: string
      maxAnalyze?: number // Default 10, max 20
    }
  ): Promise<AnalyzeDeadEndsResponse> {
    return await this.apiClient.post<AnalyzeDeadEndsResponse>('/itinerary/campsites/analyze', {
      deadEnds,
      tripId: options?.tripId,
      maxAnalyze: options?.maxAnalyze
    })
  }

  // Itinerary waypoint methods
  async getItineraryWaypoints(tripId: string, routeId: string): Promise<WaypointResponse[]> {
    return await this.apiClient.get<WaypointResponse[]>(`/itinerary/trips/${tripId}/routes/${routeId}/itinerary-waypoints`)
  }

  async reorderItineraryWaypoints(tripId: string, routeId: string, waypointIds: string[]): Promise<void> {
    return await this.apiClient.patch(`/itinerary/trips/${tripId}/routes/${routeId}/waypoints/reorder`, { waypointIds })
  }

  async addWaypointFromMap(
    tripId: string,
    routeId: string,
    coordinates: { latitude: number; longitude: number },
    options?: { poi_type?: string; name?: string; order_index?: number }
  ): Promise<WaypointResponse> {
    return await this.apiClient.post<WaypointResponse>(
      `/itinerary/trips/${tripId}/routes/${routeId}/waypoints/from-map`,
      {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        ...options
      }
    )
  }

}

// POI Result interface
export interface POIResult {
  id: string
  name: string
  category: string
  poi_type: string
  latitude: number
  longitude: number
  address?: string
  distance?: number // Distance from route in meters
  metadata?: {
    phone?: string
    hours?: string
    rating?: number
    website?: string
    description?: string
  }
}

// Dead End Discovery interfaces
// Dispersed campsite finder – viewport GeoJSON returned by /itinerary/explore-campsites
export interface ExploreCampsiteFeature {
  type: 'Feature'
  id: string
  geometry: { type: 'Point'; coordinates: [number, number] } // [lng, lat]
  properties: {
    roadName: string | null
    classification: 'dead_end' | 'turn'
    distanceFromMainRoad: number | null // meters
    landDesignation: 'usfs' | 'blm' | null
  }
}

export interface ExploreCampsitesResponse {
  type: 'FeatureCollection'
  features: ExploreCampsiteFeature[]
}

export interface DeadEnd {
  coordinates: [number, number] // [lng, lat]
  roadId: string
  roadName?: string
  distanceFromMainRoad?: number // meters
  classification: 'dead_end' | 'turn'
  metadata?: {
    intersectingRoadId?: string
    intersectingRoadName?: string
    intersectionDistance?: number
    spurLength?: number
    spurAngle?: number
  }
  candidate?: CampsiteCandidate
}

export interface DeadEndResponse {
  success: boolean
  deadEnds: DeadEnd[]
  count: number
  bbox: [number, number, number, number]
  candidates?: CampsiteCandidate[]
  visualization?: {
    bbox: [number, number, number, number]
    bboxArea: number
    bboxSize: { width: number; height: number }
    bboxCenter: [number, number]
    searchCenter?: [number, number]
    searchRadiusMiles?: number
  }
  source?: 'cache' | 'overlapping' | 'queued' | 'live'
  cachedAt?: string
  queuedAt?: string
}

// Ranger AI Campsite Analysis types
export type CampingSuitability = 'excellent' | 'good' | 'fair' | 'poor' | 'not_recommended'

export interface RangerCampsiteAnalysis {
  candidateId: string
  coordinates: [number, number]
  suitabilityScore: number // 0-100
  analysis: {
    summary: string
    strengths: string[]
    concerns: string[]
    recommendations: string[]
    campingSuitability: CampingSuitability
    vehicleRequirements?: string[]
    permitsRequired?: string[]
    seasonalConsiderations?: string
    safetyWarnings?: string[]
  }
  aiConfidence: number // 0-1
  rawResponse?: string
}

export interface AnalyzeDeadEndsResponse {
  success: boolean
  data: {
    analyses: RangerCampsiteAnalysis[]
    analyzedCount: number
    skippedCount: number
  }
}

export type RouteGenerationStatusType = 'pending' | 'in_progress' | 'completed' | 'failed'

export interface RouteGenerationStatus {
  status: RouteGenerationStatusType
  tripId: string
  expectedDays: number[]
  completedDays: number[]
  lastUpdated: string
  error?: string
}

export interface ForestRoadValidationResponse {
  isValid: boolean
  message?: string
  road?: {
    id: string
    name?: string
    highway: string
    surface?: string
  }
  distance?: number // meters
  snappedCoordinates?: [number, number] // [lng, lat]
  error?: string
  upgradeRequired?: boolean
  tier?: string
}
